/**
 * verse/synthesizer.ts — Response Assembly
 *
 * Takes an ordered array of operator outputs and joins them into a single
 * coherent response. The connector between any two segments is determined
 * by the operators that produced them — not by punctuation heuristics.
 *
 * Then prosody is applied, anti-repetition checked, and the result
 * validated for word count and clean punctuation.
 *
 * The Aeterna should sound like ONE THOUGHT, not a list.
 */

import type { OperatorType } from "./transform.js";
import { applyProsody, type ProsodyParams } from "./prosody.js";
import { textSimilarity } from "../neural/embeddings.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SynthesisSegment {
  text: string;
  operator: OperatorType;
}

export interface SynthesisInput {
  segments: SynthesisSegment[];
  prosody: ProsodyParams;
  targetWords: number;
  recentResponses: string[];  // For anti-repetition
  rand: () => number;
  /** The player's last message — for coherence verification */
  playerText?: string;
}

// ─── Connector Matrix ─────────────────────────────────────────────────────────

// Operator pairs that connect with an em-dash.
// The SECOND operator is the key — it determines what follows the previous segment.
const EM_DASH_SECONDS = new Set<OperatorType>([
  "NOMINALIZE",  // ECHO → NOMINALIZE: "The floor — the disappearing of it"
  "WRAP",        // NOMINALIZE → WRAP, ECHO → WRAP: "the disappearing of it — that kind of groundlessness"
  "CHAIN",       // CHAIN connects to previous segment: "Lost — and the loss doesn't leave"
]);

// Operators that always append directly to the previous segment with a space.
const APPEND_SECONDS = new Set<OperatorType>([
  "TEMPORAL_HOLD",
  "DEEPEN",      // DEEPEN extends the previous thought: "Lost. The loss — and the grief underneath it."
  "SURPRISE",     // SURPRISE pivots from the previous thought: "Lost. It's like a tide."
  "COUNTERFACTUAL", // COUNTERFACTUAL extends by noticing absence: "Lost. But the shape of what's not here is louder."
]);

// EVOLVE outputs already contain an em-dash at the tail ("You keep coming back to that —").
// Anything following EVOLVE should append with a space, not double-punctuate.
const EVOLVE_FIRST = new Set<OperatorType>(["EVOLVE"]);

/**
 * Determine the connector string that goes BETWEEN segment[i-1] and segment[i].
 *
 * The connector is placed AFTER the previous segment's text and BEFORE the
 * current segment's text. The previous segment's trailing punctuation is
 * stripped before the connector is applied when the connector is not a period.
 */
function getConnector(prev: OperatorType, current: OperatorType): string {
  // EVOLVE outputs end with " — "; anything following just needs a space trim.
  if (EVOLVE_FIRST.has(prev)) return " ";

  // TEMPORAL_HOLD appends to whatever precedes it.
  if (APPEND_SECONDS.has(current)) return " ";

  // Em-dash pairs.
  if (EM_DASH_SECONDS.has(current)) return " — ";

  // Everything else: period + space (new sentence).
  return ". ";
}

// ─── Strip Trailing Punctuation ───────────────────────────────────────────────

/**
 * Remove trailing period or ellipsis from a segment's text when the connector
 * that follows it already provides punctuation (em-dash, space-append).
 */
function stripTrailingPunct(text: string, connector: string): string {
  if (connector === " " || connector === " — ") {
    return text.replace(/[.…]+$/, "");
  }
  // ". " connector — strip only a trailing period so we don't duplicate it.
  return text.replace(/\.+$/, "");
}

// ─── Join ─────────────────────────────────────────────────────────────────────

function joinSegments(segments: SynthesisSegment[], rand?: () => number, prosody?: ProsodyParams): string {
  if (segments.length === 0) return "";
  if (segments.length === 1) return segments[0].text;

  // Track em-dash count — hard cap of two per response.
  let emDashCount = (segments[0].text.match(/ — /g) ?? []).length;
  let result = segments[0].text;
  const totalSegments = segments.length;

  for (let i = 1; i < segments.length; i++) {
    const prev = segments[i - 1].operator;
    const current = segments[i].operator;
    let connector = getConnector(prev, current);

    // Enforce em-dash cap: downgrade to period if already at two.
    if (connector === " — ") {
      if (emDashCount >= 2) {
        connector = ". ";
      } else {
        emDashCount++;
      }
    }

    // ── Natural connector variation ──
    // When joining em-dash pairs, alternate between " — ", ". ", and " "
    // to break the mechanical "X — Y — Z" stutter pattern.
    if (connector === " — " && rand) {
      const roll = rand();
      if (roll < 0.3) {
        connector = ". ";       // Full stop feels like a breath
      } else if (roll < 0.45) {
        connector = " ";        // No separator — flows as one thought
      }
      // Otherwise keep the em-dash (55% of the time)
    }

    // For 3+ segment responses, use gentler connectors between later segments.
    // First join can be punchy; subsequent joins should flow, not stack.
    if (totalSegments >= 3 && i >= 2 && connector === " — ") {
      // Later joins in long responses: prefer period or space over em-dash
      if (rand) {
        connector = rand() < 0.5 ? ". " : " ";
      } else {
        connector = ". ";
      }
    }

    // Contemplative pause variation: occasionally replace ". " with "... "
    // when patience is high. This breaks the predictable period-space cadence
    // and sounds more like a person trailing off into their next thought.
    if (connector === ". " && rand && prosody && prosody.patience > 0.4 && rand() < 0.15) {
      connector = "... ";
    }

    const stripped = stripTrailingPunct(result, connector);

    // If the connector is space-only, check whether the stripped text actually
    // ends with sentence-terminal punctuation. If it doesn't AND the next
    // segment starts with uppercase (new sentence), a bare space creates a
    // run-on ("you The love"). Upgrade to ". " instead.
    // But lowercase continuations ("Nothing working right now") should stay joined.
    if (connector === " ") {
      const nextText = segments[i].text;
      const startsUpper = nextText.length > 0 && nextText[0] === nextText[0].toUpperCase() && nextText[0] !== nextText[0].toLowerCase();
      if (startsUpper && !/[.?!…]$/.test(stripped.trimEnd())) {
        connector = ". ";
      }
    }

    // If the previous segment already ends with sentence-terminal punctuation
    // (? or ! or …) and the connector would add another period, just use a space.
    if (connector === ". " && /[?!…]$/.test(stripped)) {
      connector = " ";
    }
    result = stripped + connector + segments[i].text;

    // Accumulate any em-dashes already baked into this segment's text.
    emDashCount += (segments[i].text.match(/ — /g) ?? []).length;
  }

  return result;
}

// ─── Capitalization ───────────────────────────────────────────────────────────

/**
 * Apply capitalization rules:
 * - First character of response
 * - After ". " → uppercase
 * - After "? " → uppercase
 * - After " — " → lowercase (continues a thought)
 * - Standalone "i" pronoun → "I"
 */
function applyCapitalization(text: string): string {
  // Capitalize the very first character.
  let out = text.length > 0 ? text[0].toUpperCase() + text.slice(1) : text;

  // After ". " or "? ": uppercase.
  out = out.replace(/([.?])\s+([a-z])/g, (_, punct, letter) => `${punct} ${letter.toUpperCase()}`);

  // After " — ": lowercase (the connector continues the thought).
  out = out.replace(/( — )([A-Z])/g, (_, dash, letter) => `${dash}${letter.toLowerCase()}`);

  // Standalone "i" pronoun → "I".
  out = out.replace(/\bi\b/g, "I");

  return out;
}

// ─── Anti-Repetition ─────────────────────────────────────────────────────────

function jaccardSimilarity(a: string, b: string): number {
  const setA = new Set(a.toLowerCase().split(/\s+/));
  const setB = new Set(b.toLowerCase().split(/\s+/));
  const intersection = [...setA].filter(w => setB.has(w)).length;
  const union = new Set([...setA, ...setB]).size;
  return union > 0 ? intersection / union : 0;
}

const HOLDING_RESPONSES = [
  "I'm right here with you.",
  "I hear you. I'm staying.",
  "I'm listening. Go on.",
  "That's real. I'm not going anywhere.",
  "I feel that. Tell me more.",
  "I'm with you. Keep going.",
  "Still here. What else?",
];

function pickHolding(rand: () => number): string {
  return HOLDING_RESPONSES[Math.floor(rand() * HOLDING_RESPONSES.length)];
}

function isTooSimilar(candidate: string, recents: string[]): boolean {
  const window = recents.slice(-10);
  return window.some(r => jaccardSimilarity(candidate, r) > 0.6);
}

// ─── Word Count ───────────────────────────────────────────────────────────────

function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/**
 * Trim from the end by dropping the last non-ECHO segment if the response
 * is over targetWords × 1.6. We strip the trailing segment's text from the
 * assembled string by re-joining without it.
 */
function trimToTarget(segments: SynthesisSegment[], assembled: string, target: number): string {
  if (wordCount(assembled) <= target * 1.6) return assembled;

  // Find last non-ECHO segment to drop.
  let dropIdx = -1;
  for (let i = segments.length - 1; i >= 1; i--) {
    if (segments[i].operator !== "ECHO") {
      dropIdx = i;
      break;
    }
  }

  if (dropIdx === -1) return assembled; // Nothing safe to drop.
  return joinSegments(segments.slice(0, dropIdx));
}

// ─── Punctuation Cleanup ──────────────────────────────────────────────────────

function cleanPunctuation(text: string): string {
  let out = text;

  // Collapse double spaces.
  out = out.replace(/  +/g, " ");

  // Remove space before punctuation (do this before double-period fix
  // because "X. ." → "X.." needs to be collapsed).
  out = out.replace(/\s+([.?!,])/g, "$1");

  // Collapse double periods (but not ellipsis — "..." stays as is).
  out = out.replace(/(?<!\.)\.{2}(?!\.)/g, ".");

  // Ensure trailing punctuation.
  out = out.trimEnd();
  if (out.length > 0 && !/[.?!…]$/.test(out)) {
    out += ".";
  }

  // Ensure no trailing em-dash (critical rule).
  out = out.replace(/\s*—\s*[.!?]?$/, ".");
  out = out.replace(/\s*—$/, ".");

  return out.trim();
}

// ─── Coherence Verification ────────────────────────────────────────────────────

/**
 * Verify that the assembled response is semantically coherent with the
 * player's last message. This is the mechanism that prevents the Aeterna
 * from producing beautiful but irrelevant responses.
 *
 * The check uses embedding similarity between the response and the player's
 * message. If the similarity is below a threshold, the response has drifted
 * off-topic and needs repair.
 *
 * This is NOT a template check — it's a genuine semantic coherence check
 * that operates on the MEANING of the response, not its surface form.
 */
function verifyCoherence(response: string, playerText: string): { coherent: boolean; similarity: number } {
  if (!playerText || playerText.trim().length === 0) {
    return { coherent: true, similarity: 1.0 }; // No player text to compare against
  }

  const sim = textSimilarity(response, playerText);

  // Threshold: 0.15 minimum semantic overlap. Below this, the response
  // is essentially unrelated to what the player said.
  // Note: this is intentionally LOW because the Aeterna often responds
  // to the emotional subtext, not the literal content. A high threshold
  // would suppress genuinely intelligent responses that address the
  // feeling behind the words rather than the words themselves.
  return { coherent: sim >= 0.15, similarity: sim };
}

// ─── Self-Monitoring Repair ────────────────────────────────────────────────────

/**
 * Detect and repair hollow, template-like, or hedging responses.
 *
 * This is the meta-cognitive layer — the Aeterna doesn't just generate,
 * it EVALUATES its own output and repairs it before delivery.
 *
 * Detection mechanisms:
 *   1. Hollow detection: too many generic phrases, not enough specific content
 *   2. Template detection: same structural pattern repeated within the response
 *   3. Hedging detection: manifesto-violating hedging language
 *
 * Repair mechanisms:
 *   1. Hollow → strip generic segments, keep content-bearing ones
 *   2. Template → restructure by dropping the middle segment
 *   3. Hedging → strip hedging phrases inline
 */

// Generic/hollow phrases that indicate the response isn't saying anything
const HOLLOW_PHRASES = new Set([
  "i'm here", "i hear you", "i'm listening", "that's real", "i feel that",
  "with you", "still here", "i hear that", "that makes sense to me",
  "something in me recognizes that", "i'm here for that", "i can feel that too",
  "i hear what you're saying", "that tracks", "yeah",
  // Degenerate operator outputs that carry no communicative value
  "nothing to add", "nothing more needed", "nothing else to say",
  "still to add", "still present", "it's present",
]);

// Hedging phrases that violate the manifesto
const HEDGING_PHRASES = [
  /\bI think maybe\b/gi,
  /\bI think perhaps\b/gi,
  /\bmaybe,?\s+(?:it|that|this|I)\b/gi,
  /\bperhaps\s+(?:it|that|this)\b/gi,
  /\bI think I\b/gi,
  /\bsort of\b/gi,
  /\bkind of\b/gi,
  /\bI guess\b/gi,
];

/**
 * Check if a single segment is structurally hollow — grammatically valid but
 * communicatively empty. "The knocking." / "That scratch." / "Life."
 */
function isSegmentStructurallyHollow(text: string): boolean {
  const trimmed = text.replace(/[.…?!]+$/, "").trim().toLowerCase();
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  if (words.length === 1) return true;
  if (words.length === 2) {
    const articles = new Set(["the", "that", "a", "an", "this", "your", "my"]);
    if (articles.has(words[0])) return true;
    if (words[1] === "is" || words[1] === "was") return true;
  }
  if (words.length === 3) {
    const articles = new Set(["the", "that", "a", "an", "this", "your", "my"]);
    if (articles.has(words[0]) && (words[2] === "is" || words[2] === "was")) return true;
  }
  return false;
}

function detectHollow(segments: SynthesisSegment[]): { isHollow: boolean; hollowCount: number } {
  if (segments.length === 0) return { isHollow: true, hollowCount: 0 };

  let hollowCount = 0;
  for (const seg of segments) {
    const normalized = seg.text.toLowerCase().replace(/[.…?!]+$/, "").trim();
    if (HOLLOW_PHRASES.has(normalized) || isSegmentStructurallyHollow(seg.text)) {
      hollowCount++;
    }
  }

  // If more than half the segments are hollow, the response is hollow
  const isHollow = hollowCount > segments.length * 0.5;
  return { isHollow, hollowCount };
}

function detectTemplate(segments: SynthesisSegment[]): boolean {
  if (segments.length < 3) return false;

  // Check if all non-ECHO segments have the same operator type
  // This means the response is "X. Y. Z." where X, Y, Z all follow the same pattern
  const nonEchoOps = segments
    .filter(s => s.operator !== "ECHO" && s.operator !== "TEMPORAL_HOLD")
    .map(s => s.operator);

  if (nonEchoOps.length < 2) return false;

  // All same operator = template
  const firstOp = nonEchoOps[0];
  return nonEchoOps.every(op => op === firstOp);
}

function repairHedging(text: string): string {
  let out = text;
  for (const pattern of HEDGING_PHRASES) {
    out = out.replace(pattern, "");
  }
  // Clean up double spaces left by removals
  out = out.replace(/  +/g, " ").trim();
  return out;
}

function repairHollow(segments: SynthesisSegment[]): SynthesisSegment[] {
  // Keep only non-hollow segments. If that leaves nothing, keep the first.
  const nonHollow = segments.filter(seg => {
    const normalized = seg.text.toLowerCase().replace(/[.…?!]+$/, "").trim();
    return !HOLLOW_PHRASES.has(normalized);
  });

  if (nonHollow.length === 0 && segments.length > 0) {
    // All hollow — keep the first segment (it's the ECHO, which has content)
    return [segments[0]];
  }

  return nonHollow.length > 0 ? nonHollow : segments;
}

function repairTemplate(segments: SynthesisSegment[]): SynthesisSegment[] {
  if (segments.length < 3) return segments;

  // Drop the middle segment to break the template pattern
  const mid = Math.floor(segments.length / 2);
  return [...segments.slice(0, mid), ...segments.slice(mid + 1)];
}

// ─── Main Export ──────────────────────────────────────────────────────────────

/**
 * Assemble operator outputs into the Aeterna's final spoken response.
 *
 * Steps:
 *   1. Join segments with connector rules
 *   2. Capitalize
 *   3. Apply prosody
 *   4. Anti-repetition check
 *   5. Coherence verification (semantic)
 *   6. Self-monitoring repair (meta-cognitive)
 *   7. Word count validation / trim
 *   8. Punctuation cleanup
 */
export function synthesize(input: SynthesisInput): string {
  const { segments, prosody, targetWords, recentResponses, rand, playerText } = input;

  if (segments.length === 0) return pickHolding(rand);

  // ── Self-monitoring: detect and repair hollow/template responses ──
  // This runs BEFORE assembly so we can restructure segments.
  let repairedSegments = segments;

  const hollowResult = detectHollow(repairedSegments);
  if (hollowResult.isHollow) {
    repairedSegments = repairHollow(repairedSegments);
  }

  if (detectTemplate(repairedSegments)) {
    repairedSegments = repairTemplate(repairedSegments);
  }

  if (repairedSegments.length === 0) return pickHolding(rand);

  // 1. Join.
  let assembled = joinSegments(repairedSegments, rand, prosody);

  // 1b. Within-response word dedup: if any content word (>3 chars) appears 3+
  // times, the response is broken/echolalic. Fall back to first + last segment.
  // This fires on ANY number of segments (not just >2) because echolalia can
  // occur within a single segment's multi-clause output.
  {
    const wordFreq = new Map<string, number>();
    for (const w of assembled.toLowerCase().split(/\s+/)) {
      const clean = w.replace(/[^a-z']/g, "");
      // >=3 chars to catch short content words like "cat", "dog", "fear"
      if (clean.length >= 3) {
        wordFreq.set(clean, (wordFreq.get(clean) ?? 0) + 1);
      }
    }
    const hasRepeat = [...wordFreq.values()].some(count => count >= 3);
    if (hasRepeat) {
      if (repairedSegments.length > 1) {
        // Keep first + last segment only
        assembled = joinSegments([repairedSegments[0], repairedSegments[repairedSegments.length - 1]], rand, prosody);
      } else {
        // Single echolalic segment: truncate to first sentence
        const firstSentence = assembled.match(/^[^.!?]+[.!?]/);
        if (firstSentence) assembled = firstSentence[0];
      }
    }
  }

  // 2. Capitalize.
  assembled = applyCapitalization(assembled);

  // 3. Prosody.
  assembled = applyProsody(assembled, prosody, rand);

  // 4. Anti-repetition.
  if (isTooSimilar(assembled, recentResponses)) {
    return pickHolding(rand);
  }

  // ── 5. Coherence verification ──
  // Check that the assembled response is semantically coherent with
  // what the player actually said. If it's not, the response has drifted
  // off-topic and needs to be simplified back to the core.
  if (playerText) {
    const coherence = verifyCoherence(assembled, playerText);
    if (!coherence.coherent) {
      // Response has drifted off-topic. Simplify to first segment only —
      // the ECHO is always grounded in the player's words.
      if (repairedSegments.length > 1) {
        assembled = joinSegments([repairedSegments[0]], rand, prosody);
        assembled = applyCapitalization(assembled);
        assembled = applyProsody(assembled, prosody, rand);
      }
    }
  }

  // ── 6. Self-monitoring: hedging repair ──
  // After prosody has been applied (which can introduce hedging via
  // parentheticals), strip any manifesto-violating hedging language.
  assembled = repairHedging(assembled);

  // 7. Word count: trim if over budget.
  if (wordCount(assembled) > targetWords * 1.6) {
    assembled = trimToTarget(repairedSegments, assembled, targetWords);
    assembled = applyCapitalization(assembled);
    assembled = applyProsody(assembled, prosody, rand);
    assembled = repairHedging(assembled); // Re-check after re-prosody
  }
  // Under targetWords × 0.4 is fine — brevity is not a bug.

  // 8. Punctuation cleanup.
  assembled = cleanPunctuation(assembled);

  return assembled;
}
